export type AggregationDef = {
  key: string;
  labelKey: string;
  fn: (values: number[]) => number;
  supportedKinds: ('numeric' | 'categorical')[];
};

function calcSum(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((acc, v) => acc + v, 0);
}

function calcAvg(values: number[]): number {
  if (values.length === 0) return 0;
  return calcSum(values) / values.length;
}

function calcMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function calcPercentile(values: number[], pct: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.floor(sorted.length * pct);
  // Clamp to last index
  return sorted[Math.min(index, sorted.length - 1)];
}

export const aggregationRegistry: AggregationDef[] = [
  {
    key: 'sum',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.sum',
    fn: calcSum,
    supportedKinds: ['numeric'],
  },
  {
    key: 'avg',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.avg',
    fn: calcAvg,
    supportedKinds: ['numeric'],
  },
  {
    key: 'mean',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.mean',
    fn: calcAvg,
    supportedKinds: ['numeric'],
  },
  {
    key: 'median',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.median',
    fn: calcMedian,
    supportedKinds: ['numeric'],
  },
  {
    key: 'min',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.min',
    fn: (values) => (values.length === 0 ? 0 : Math.min(...values)),
    supportedKinds: ['numeric'],
  },
  {
    key: 'max',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.max',
    fn: (values) => (values.length === 0 ? 0 : Math.max(...values)),
    supportedKinds: ['numeric'],
  },
  {
    key: 'count',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.count',
    fn: (values) => values.length,
    supportedKinds: ['numeric', 'categorical'],
  },
  {
    key: 'p90',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.p90',
    fn: (values) => calcPercentile(values, 0.9),
    supportedKinds: ['numeric'],
  },
  {
    key: 'p95',
    labelKey: 'analytics.dashboard.complexCharts.configuration.aggregations.p95',
    fn: (values) => calcPercentile(values, 0.95),
    supportedKinds: ['numeric'],
  },
];

export type AggregationKey = (typeof aggregationRegistry)[number]['key'];
